import { createSlice, createAsyncThunk, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { Position } from '@kemu-io/kemu-core/types';
import { RootState } from '../../app/store';
import { AsyncState, AsyncRequestStatus, SelectedBlockInfo } from '../../types/core_t';
import recipeApi from '../../api/recipe/recipeApi';
import { storeRecipeReducer } from './reducers/storeRecipeReducer';
import { setRecipeUploadProgressReducer } from './reducers/setuploadProgressReducer';
import { downloadRecipeActionReducer } from './reducers/downloadRecipeReducer';
import localStorageManager from '@common/localStorageManager';
import {
	ColorScheme,
	themeSettingsStorageKey,
	type ThemeSettings,
} from '@common/theme/themeUtils';

interface AsyncOperationDetails extends AsyncState {
	action?: string;
	entityId?: string;
	extra?: unknown;
}

export type NotificationInfo = {
	/** optional unique error code */
	code?: string;
	showProgress?: boolean;
	message: string;
	title?: string;
	duration?: number;
	type: 'error' | 'success' | 'info' | 'warning';
}

interface AsyncOperationWithProgress extends AsyncOperationDetails {
	contentProgress?: number;
	storageProgress?: number;
	noProgress?: boolean;
}

export interface SettingsPanelInfo {
	block: SelectedBlockInfo
}

export interface PhoneInfo {
	name: string;
}

export interface CreateSnippetModalInfo {
	snippetCode: string;
	snippetId?: string;
}

export type UserActions = Record<string, boolean>;

export type LogicMapperSettings = {
	portInspectorVisible: boolean;
	showConnectionAnimations: boolean;
	showWidgetProcessingAnimations: boolean;
	showSuccessTick: boolean;
	showDebuggingPanel: boolean;
	showStatusPanel: boolean;
	showGlobalVariablesPanel: boolean;
	removeBackgroundPattern: boolean;
	hidePortLabels: boolean;
	/** Position of the debugging panel */
	debuggingPanelPosition: Position;
	/** Position of the status panel */
	statusPanelPosition: Position;
	/** Size of the status panel */
	statusPanelSize: { width: number, height: number };
	/** Position of the global variables panel */
	globalVariablesPanelPosition: Position;
	/** Size of the global variables panel */
	globalVariablesPanelSize: { width: number, height: number };
	/** Position of the port inspector */
	portInspectorPosition: Position;
	/** Size of the port inspector */
	portInspectorSize: { width: number, height: number };
	/** Position of the service help panel */
	serviceHelpPanelPosition: Position;
	/** Size of the service help panel */
	serviceHelpPanelSize: { width: number, height: number };
	/** Whether the port inspector is minimized */
	portInspectorMinimized: boolean;
	/** Whether the global variables panel is minimized */
	globalVariablesPanelMinimized: boolean;
	/** Whether the status panel is minimized */
	statusPanelMinimized: boolean;
	/** Whether to save global variable values when saving recipes */
	saveGlobalVariableValues: boolean;
}

type TutorialInfo = {
	isDraft: boolean;
	id: string;
	notificationShown: boolean;
	step: number;
}

const logicMapperSettingsKey = 'logicMapperSettings';

export interface InterfaceState {
	newVersionAvailable: boolean;
	colorScheme: ColorScheme;
	saveAsModalVisible: boolean;
	missingServicesModalVisible: boolean;
	secretMappingModalVisible: boolean;
	premiumUpgradeModalVisible: boolean;
	hubApiKeyModalVisible: boolean;
	userProfileModalVisible: string | null;
	asyncOperation: AsyncOperationDetails | null;
	recipeUploadDetails: (AsyncOperationWithProgress & { hasStorage?: boolean }) | null;
	recipeDownloadDetails: AsyncOperationWithProgress | null;
	twilioConnectionToken: string | null;
	connectedPhone: PhoneInfo | null;
	// activeTutorialInfo: TutorialInfo | null;
	logicMapperSettings: LogicMapperSettings;
	/** Will contain the checksum of the combined storage disk (if any) */
	lastStorageChecksum: string | null;

	settingsPanel: SettingsPanelInfo | null;
	userActions: UserActions;
	notification: NotificationInfo | null;
	createSnippetModal: CreateSnippetModalInfo | null;
	welcomeMessagePopupVisible: boolean;
	deployRecipeModalVisible: boolean;
	manageEnvironmentsModalVisible: boolean;
	widgetLauncherVisible: boolean;
	widgetLauncherTargetPosition: Position | null;
}

const initialState: InterfaceState = {
	newVersionAvailable: false,
	colorScheme: localStorageManager.getStoredObject<ThemeSettings>(themeSettingsStorageKey, {
		colorScheme: 'light',
	}).colorScheme,
	saveAsModalVisible: false,
	missingServicesModalVisible: false,
	secretMappingModalVisible: false,
	premiumUpgradeModalVisible: false,
	hubApiKeyModalVisible: false,
	deployRecipeModalVisible: false,
	widgetLauncherVisible: false,
	widgetLauncherTargetPosition: null,
	userProfileModalVisible: null,
	logicMapperSettings: localStorageManager.getStoredObject<LogicMapperSettings>(logicMapperSettingsKey, {
		portInspectorVisible: true,
		showConnectionAnimations: true,
		showWidgetProcessingAnimations: true,
		showSuccessTick: true,
		showDebuggingPanel: false,
		showStatusPanel: true,
		showGlobalVariablesPanel: true,
		removeBackgroundPattern: false,
		hidePortLabels: false,
		debuggingPanelPosition: {
			x: 20,
			y: 65,
		},
		statusPanelPosition: {
			x: 20,
			y: 115,
		},
		statusPanelSize: {
			width: 300,
			height: 150,
		},
		globalVariablesPanelPosition: {
			x: 20,
			y: 165,
		},
		globalVariablesPanelSize: {
			width: 300,
			height: 150,
		},
		portInspectorPosition: {
			x: 20,
			y: 135,
		},
		portInspectorSize: {
			width: 300,
			height: 200,
		},
		serviceHelpPanelPosition: {
			x: 50,
			y: 50,
		},
		serviceHelpPanelSize: {
			width: 300,
			height: 350,
		},
		portInspectorMinimized: true,
		globalVariablesPanelMinimized: true,
		statusPanelMinimized: true,
		saveGlobalVariableValues: true,
	}),
	welcomeMessagePopupVisible: false,
	recipeUploadDetails: null,
	recipeDownloadDetails: null,
	asyncOperation: null,
	lastStorageChecksum: null,
	twilioConnectionToken: null,
	connectedPhone: null,
	settingsPanel: null,
	// activeTutorialInfo: null,
	notification: null,
	createSnippetModal: null,
	manageEnvironmentsModalVisible: false,
	/** 
	 * Ephemeral actions that are only preserved for the duration of the session. 
	 * They are reset when the page is refreshed.
	 **/
	userActions: {

	}
};


const removeRecipe = createAsyncThunk('/interface/removeRecipe', async (props: {recipeId: string, onSuccess?: () => void}): Promise<string | null> => {
	await recipeApi.deleteRecipe(props.recipeId);
	props.onSuccess?.();
	// To reset the storage checksum
	return null;
});



/**
 * Reduces builder plate for basic async actions that just show an indicator
 * @param builder 
 * @param thunk 
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildAsyncReducerBoilerplate = (builder: ActionReducerMapBuilder<InterfaceState>, thunk: any) => {
	builder.addCase(thunk.pending, (state) => {
		state.asyncOperation = {
			...state.asyncOperation,
			status: AsyncRequestStatus.loading,
			error: undefined
		};
	});

	builder.addCase(thunk.fulfilled, (state, action: PayloadAction<string | null >) => {
		state.asyncOperation = { ...state.asyncOperation, status: AsyncRequestStatus.completed };
		state.lastStorageChecksum = action.payload;
	});

	builder.addCase(thunk.rejected, (state, action) => {
		state.asyncOperation = {
			...state.asyncOperation,
			status: AsyncRequestStatus.error,
			error: action.error
		};
	});
};

export const interfaceSlice = createSlice({
	name: 'workspace',
	initialState,
	reducers: {
		setRecipeUploadProgress: setRecipeUploadProgressReducer,

		showGlobalNotification: (state, action: PayloadAction<NotificationInfo | null>) => {
			state.notification = action.payload;
		},

		setNewVersionAvailable: (state) => {
			state.newVersionAvailable = true;
		},

		/**
		 * Stores the active application color scheme and persists it locally.
		 */
		setColorScheme: (state, action: PayloadAction<ColorScheme>) => {
			if (state.colorScheme === action.payload) {
				return;
			}

			state.colorScheme = action.payload;
			localStorageManager.updateObject(themeSettingsStorageKey, {
				colorScheme: action.payload,
			});
		},

		/**
		 * Toggles the active application color scheme and persists the new value.
		 */
		toggleColorScheme: (state) => {
			const nextColorScheme: ColorScheme =
				state.colorScheme === 'dark' ? 'light' : 'dark';

			state.colorScheme = nextColorScheme;
			localStorageManager.updateObject(themeSettingsStorageKey, {
				colorScheme: nextColorScheme,
			});
		},

		/** 
		 * Changes the current details of the tutorial. 
		 * Only the provided properties will be replaced.
		 **/
		// setActiveTutorialInfo: (state, action: PayloadAction<TutorialInfo | null>) => {
		// 	state.activeTutorialInfo = action.payload;
		// },

		setConnectedPhoneInfo: (state, action: PayloadAction<PhoneInfo | null>) => {
			state.connectedPhone = action.payload;
		},
		setWelcomeMessagePopupVisible: (state, action: PayloadAction<boolean>) => {
			state.welcomeMessagePopupVisible = action.payload;
		},

		setTwilioConnectionToken: (state, action: PayloadAction<string>) => {
			state.twilioConnectionToken = action.payload;
		},

		setBlockForSettingsPanel: (state, action: PayloadAction<SettingsPanelInfo | null>) => {
			state.settingsPanel = action.payload;
		},

		setLogicMapperSettingValue: (state, action: PayloadAction<{key: keyof LogicMapperSettings, value: LogicMapperSettings[keyof LogicMapperSettings]}>) => {
			const currentValue = state.logicMapperSettings[action.payload.key];
			const newValue = action.payload.value;

			// Check if value actually changed to prevent unnecessary re-renders
			// For objects, do shallow comparison
			let hasChanged = false;
			if (currentValue === newValue) {
				hasChanged = false;
			} else if (typeof currentValue === 'object' && currentValue !== null && typeof newValue === 'object' && newValue !== null) {
				// Shallow comparison for objects (e.g., {x, y} or {width, height})
				const currentKeys = Object.keys(currentValue);
				const newKeys = Object.keys(newValue);
				if (currentKeys.length !== newKeys.length) {
					hasChanged = true;
				} else {
					hasChanged = currentKeys.some(key => (currentValue as any)[key] !== (newValue as any)[key]);
				}
			} else {
				hasChanged = true;
			}

			// Only update if value actually changed
			if (!hasChanged) {
				return;
			}

			const newSettings: LogicMapperSettings = {
				...state.logicMapperSettings,
				[action.payload.key]: action.payload.value
			};

			state.logicMapperSettings = newSettings;
			localStorageManager.updateObject(logicMapperSettingsKey, newSettings);
		},

		// setShowPortDebugPanel: (state, action: PayloadAction<boolean>) => {
		// 	const newSettings: LogicMapperSettings = {
		// 		...state.logicMapperSettings,
		// 		debugPanelVisible: action.payload
		// 	};

		// 	state.logicMapperSettings = newSettings;
		// 	localStorageManager.updateObject(logicMapperSettingsKey, newSettings);
		// },

		setAsyncOperationDetails: (state, action: PayloadAction<Partial<AsyncOperationDetails>>) => {
			const details = {
				...state.asyncOperation,
				...action.payload
			};

			state.asyncOperation = <AsyncOperationDetails> details;
		},

		showSaveAsDialog: (state, action: PayloadAction<boolean>) => {
			state.saveAsModalVisible = action.payload;
		},

		showMissingServicesModal: (state, action: PayloadAction<boolean>) => {
			state.missingServicesModalVisible = action.payload;
		},

		showSecretMappingModal: (state, action: PayloadAction<boolean>) => {
			state.secretMappingModalVisible = action.payload;
		},

		showHubApiKeyModal: (state, action: PayloadAction<boolean>) => {
			state.hubApiKeyModalVisible = action.payload;
		},

		showPremiumUpgradeModal: (state, action: PayloadAction<boolean>) => {
			state.premiumUpgradeModalVisible = action.payload;
		},

		showUserProfileModal: (state, action: PayloadAction<string | null>) => {
			state.userProfileModalVisible = action.payload;
		},

		setUserAction: (state, action: PayloadAction<{ action: keyof UserActions, value: boolean }>) => {
			state.userActions = {
				...state.userActions,
				[action.payload.action]: action.payload.value
			};
		},

		setRecipeDownloadProgress: (state, action: PayloadAction<{content?: number; storage?: number}>) => {
			if (state.recipeDownloadDetails) {
				if (action.payload.content) {
					state.recipeDownloadDetails.contentProgress = action.payload.content;
				}
				if (action.payload.storage) {
					state.recipeDownloadDetails.storageProgress = action.payload.storage;
				}
			}
		},

		showCreateSnippetModal: (state, action: PayloadAction<CreateSnippetModalInfo | null>) => {
			state.createSnippetModal = action.payload;
		},

		showDeployRecipeModal: (state, action: PayloadAction<boolean>) => {
			state.deployRecipeModalVisible = action.payload;
		},

		showManageEnvironmentsModal: (state, action: PayloadAction<boolean>) => {
			state.manageEnvironmentsModalVisible = action.payload;
		},

		showWidgetLauncher: (state, action: PayloadAction<boolean | { visible: boolean, position?: Position | null }>) => {
			if (typeof action.payload === 'boolean') {
				state.widgetLauncherVisible = action.payload;
				if (!action.payload) {
					state.widgetLauncherTargetPosition = null;
				}
			} else {
				state.widgetLauncherVisible = action.payload.visible;
				state.widgetLauncherTargetPosition = action.payload.position || null;
			}
		}
	},

	extraReducers: (builder) => {
		downloadRecipeActionReducer(builder);
		storeRecipeReducer(builder);
		buildAsyncReducerBoilerplate(builder, removeRecipe);
	}
});


export const {
	showSaveAsDialog,
	showMissingServicesModal,
	showSecretMappingModal,
	showHubApiKeyModal,
	showPremiumUpgradeModal,
	showUserProfileModal,
	setRecipeUploadProgress,
	setRecipeDownloadProgress,
	setAsyncOperationDetails,
	setBlockForSettingsPanel,
	setTwilioConnectionToken,
	setConnectedPhoneInfo,
	// setActiveTutorialInfo,
	setNewVersionAvailable,
	setColorScheme,
	setUserAction,
	setLogicMapperSettingValue,
	toggleColorScheme,
	showGlobalNotification,
	showCreateSnippetModal,
	setWelcomeMessagePopupVisible,
	showDeployRecipeModal,
	showManageEnvironmentsModal,
	showWidgetLauncher,
} = interfaceSlice.actions;

export {
	removeRecipe,
};


export const selectSaveAsModalVisible = (state: RootState): boolean => state.interface.saveAsModalVisible;
export const selectMissingServicesModalVisible = (state: RootState): boolean => state.interface.missingServicesModalVisible;
export const selectSecretMappingModalVisible = (state: RootState): boolean => state.interface.secretMappingModalVisible;
export const selectHubApiKeyModalVisible = (state: RootState): boolean => state.interface.hubApiKeyModalVisible;
export const selectPremiumUpgradeModalVisible = (state: RootState): boolean => state.interface.premiumUpgradeModalVisible;
export const selectUserProfileModalVisible = (state: RootState): string | null => state.interface.userProfileModalVisible;
export const asyncOperationState = (state: RootState): AsyncOperationDetails | null => state.interface.asyncOperation;
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const selectRecipeUploadDetails = (state: RootState) => state.interface.recipeUploadDetails;
export const selectRecipeDownloadDetails = (state: RootState): AsyncOperationWithProgress | null => state.interface.recipeDownloadDetails;
export const settingsPanelDetails = (state: RootState): SettingsPanelInfo | null => state.interface.settingsPanel;
export const twilioConnectionToken = (state: RootState): string | null => state.interface.twilioConnectionToken;
export const connectedPhone = (state: RootState): PhoneInfo | null => state.interface.connectedPhone;
export const newVersionAvailable = (state: RootState): boolean => state.interface.newVersionAvailable;
export const selectColorScheme = (state: RootState): ColorScheme => state.interface.colorScheme;

export const selectNotification = (state: RootState): NotificationInfo | null => state.interface.notification;
/** Indicates if there is a tutorial linked to the current recipe */
// export const activeTutorialInfo = (state: RootState): TutorialInfo | null => state.interface.activeTutorialInfo || null;
export const selectWelcomeMessagePopupVisible = (state: RootState): boolean => state.interface.welcomeMessagePopupVisible;

export const selectUserActions = (state: RootState): Readonly<UserActions> => state.interface.userActions;
export const selectLogicMapperSettings = (state: RootState): LogicMapperSettings => state.interface.logicMapperSettings;
export const selectCreateSnippetModal = (state: RootState): CreateSnippetModalInfo | null => state.interface.createSnippetModal;
export const selectDeployRecipeModalVisible = (state: RootState): boolean => state.interface.deployRecipeModalVisible;
export const selectManageEnvironmentsModalVisible = (state: RootState): boolean => state.interface.manageEnvironmentsModalVisible;
export const selectWidgetLauncherVisible = (state: RootState): boolean => state.interface.widgetLauncherVisible;
export const selectWidgetLauncherTargetPosition = (state: RootState): Position | null => state.interface.widgetLauncherTargetPosition;
export default interfaceSlice.reducer;


